import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X } from 'lucide-react';
import { initializeAnalytics } from '../services/analyticsService';

interface CookieBannerProps {
  enabled: boolean;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ enabled }) => {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    try {
      const consent = localStorage.getItem('cookie_consent');
      if (!consent) {
        // Delay for smooth entrance after page load
        const timer = setTimeout(() => {
          setVisible(true);
          requestAnimationFrame(() => setAnimating(true));
        }, 800);
        return () => clearTimeout(timer);
      }
    } catch {
      setVisible(true);
      requestAnimationFrame(() => setAnimating(true));
    }
  }, [enabled]);

  const dismiss = (consent: 'accepted' | 'rejected') => {
    setAnimating(false);
    setTimeout(() => {
      localStorage.setItem('cookie_consent', JSON.stringify({
        consent,
        timestamp: new Date().toISOString()
      }));
      setVisible(false);
      if (consent === 'accepted') {
        initializeAnalytics();
      }
    }, 300);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 transition-all duration-300 ease-out ${
        animating
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="relative bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 via-primary-400 to-emerald-400" />

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                Privacy e Cookie
              </span>
            </div>
            <button
              onClick={() => dismiss('rejected')}
              className="p-1 -m-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Chiudi"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
            Utilizziamo cookie tecnici necessari e, con il tuo consenso, cookie analytics per migliorare il sito.{' '}
            <Link
              to="/cookies"
              className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
            >
              Scopri di più
            </Link>
          </p>

          {/* Actions */}
          <div className="flex gap-2.5">
            <button
              onClick={() => dismiss('rejected')}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Rifiuta
            </button>
            <button
              onClick={() => dismiss('accepted')}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
            >
              Accetta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
